import { type AppProps as NextAppProps } from 'next/app';
import Head from 'next/head';
import { type JSX, useMemo } from 'react';

import { CacheProvider, type EmotionCache } from '@emotion/react';

import { createEmotionCache } from '@craft/ui/util';

import { ErrorBoundary } from '@/src/components/atoms/ErrorBoundary';
import { MuiPremiumLicense } from '@/src/components-v2/config/MuiPremiumLicense';
import { Layout } from '@/src/components-v2/layout/Layout';
import {
  type AppPageComponent,
  type AppPageProps,
  type CustomAppProps,
  type GetLayout,
  type PageAuth,
} from '@/src/components-v2/types/pages';
import { DefaultPageTitle } from '@/src/config';
import { PageAuthType, PageLayoutVersion, Pages, serializablePage } from '@/src/config/pages';
import { useTTI } from '@/src/features/analytics/hooks';
import { UserProvider } from '@/src/features/auth/providers/UserProvider';
import { ApplicationConfig } from '@/src/features/config/components/ApplicationConfig';
import { EnvironmentProvider } from '@/src/features/config/providers/EnvironmentProvider';
import { logger } from '@/src/internal/logger';
import '@/styles/globals.scss';

/**
 * Client-side cache, shared for the whole session of the user in the browser.
 *
 * @see https://github.com/mui/material-ui/blob/v5.13.7/examples/material-next-ts/pages/_app.tsx
 */
const clientSideEmotionCache = createEmotionCache();

interface DeriveAuthOptions {
  readonly auth: null | PageAuth;
  readonly path: string;
}

/**
 * Derives the {@link PageAuth} for the current _app.tsx render based on the 'auth' page prop and
 * the current pathname.  The resulting 'auth' is used to both determine page-related
 * configuration properties (e.g. layout, shell rendering), auth-related properties and in the
 * case of Auth0-authenticated pages, the actual authenticated user,
 * {@link AuthenticatedUserWithSelectedRole}, that should be established in context,
 * {@link UserContext}.
 *
 * Every page type has a context wrapper that injects the 'auth' prop from the server:
 * {@link authenticatedPageContext} for Auth0-authenticated pages (which additionally bootstraps
 * the client-side authenticated user, unblocking page renders while the client-side fetch is
 * in-flight), `ltiPageContext` for LTI pages, and `publicPageContext` for public pages.  When the
 * injected 'auth' is present it is always favored.
 *
 * The pathname-based fallback below exists for the NextJS error pages, which cannot run
 * `getServerSideProps` and therefore can never inject the prop.  Any other page reaching the
 * fallback is missing its context wrapper: for Auth0 and LTI pages that is unrecoverable (no
 * user or protected environment was resolved) and nothing is rendered, while for public pages a
 * minimal 'auth' is derived from the page configuration so the page still renders, and the
 * misconfiguration is logged.
 */
const deriveAuth = (options: DeriveAuthOptions): null | PageAuth => {
  const pathname = options.path.split(/[?#]/)[0];
  const page = Pages.get({ path: pathname }, { strict: false });
  if (options.auth) {
    if (page && options.auth.page.id !== page.id) {
      logger.error(
        'The page configuration that was derived from the active pathname does not match that ' +
          "of the page that was explicitly injected by the page's server side props.  The page " +
          'configuration explicitly set on the server side props will be favored.',
        { ...options, page },
      );
    }
    return options.auth;
  }
  if (!page) {
    logger.error(
      "The page configuration was not injected from the active page's server side props and the " +
        'page could not be determined based on the pathname. This will result in fallback ' +
        'behavior where the page content is not rendered within an authenticated layout or ' +
        'context.',
      { path: options.path },
    );
    return null;
  }
  switch (page.auth) {
    case PageAuthType.Auth0: {
      logger.error(
        'The pathname is associated with an authenticated page but the page does not include ' +
          'authentication details in its server props.',
        { ...options, page },
      );
      return null;
    }
    case PageAuthType.Lti:
      logger.error(
        "The LTI page did not inject the 'auth' prop from its server side props. The page must " +
          "wrap its 'getServerSideProps' with 'ltiPageContext'; nothing can be rendered without " +
          'the injected protected environment.',
        { ...options, page },
      );
      return null;
    case PageAuthType.Public:
      if (!ErrorPageRoutes.has(pathname)) {
        logger.error(
          "The public page did not inject the 'auth' prop from its server side props. The page " +
            "must wrap its 'getServerSideProps' with 'publicPageContext'; falling back to a " +
            'pathname-derived auth without the injected environment.',
          { ...options, page },
        );
      }
      return { page: serializablePage(page), type: PageAuthType.Public };
  }
};

interface InnerAppProps<P extends Record<string, unknown>> extends NextAppProps<CustomAppProps<P>> {
  readonly auth: PageAuth;
  readonly Component: AppPageComponent<P>;
}

const InnerApp = <P extends Record<string, unknown>>({
  auth,
  Component,
  pageProps,
  router,
}: InnerAppProps<P>): JSX.Element => {
  const PageProps: AppPageProps<P> = useMemo(() => ({ ...pageProps, auth }), [pageProps, auth]);
  const getNestedLayout: GetLayout<P> = useMemo(
    () => Component.getNestedLayout ?? ((child: JSX.Element) => child),
    [Component],
  );

  const getLayout: GetLayout<P> = useMemo(() => {
    if (Component.getLayout) {
      return Component.getLayout;
    }
    return function (child: JSX.Element) {
      if (auth.page.layoutVersion === PageLayoutVersion.Version1) {
        return child;
      }
      return (
        <Layout
          isHeaderVisible={auth.page.isRenderedInShell}
          isPaddingDisabled={Component.isPaddingDisabled ?? false}
          isScrollable={
            typeof Component.isLayoutScrollable === 'function'
              ? Component.isLayoutScrollable(PageProps)
              : (Component.isLayoutScrollable ?? false)
          }
          isSidebarVisible={auth.page.isRenderedInShell && (Component.isSidebarVisible ?? true)}
        >
          <ErrorBoundary isLti={auth.type === PageAuthType.Lti}>{child}</ErrorBoundary>
        </Layout>
      );
    };
  }, [Component, PageProps, auth.page.layoutVersion, auth.type, auth.page.isRenderedInShell]);

  const children = useMemo(
    () =>
      getLayout(
        getNestedLayout(<Component {...PageProps} />, {
          pageProps: PageProps,
          router,
        }),
        { pageProps: PageProps, router },
      ),
    [Component, PageProps, getLayout, getNestedLayout, router],
  );

  switch (auth.type) {
    case PageAuthType.Auth0: {
      return (
        <>
          {/* It is critically important that the <MuiPremiumLicenseKey /> be rendered immediately
              before anything else that may rely on MUI.  @see the MuiPremiumLicense component
              for more information. */}
          <MuiPremiumLicense premiumKey={auth.muiPremiumKey} />
          <ApplicationConfig initialViewportSeed={auth.initialViewportSeed}>
            <UserProvider auth={auth}>{children}</UserProvider>
          </ApplicationConfig>
        </>
      );
    }
    case PageAuthType.Lti:
      return (
        <ApplicationConfig initialViewportSeed={auth.initialViewportSeed}>
          <UserProvider auth={auth}>{children}</UserProvider>
        </ApplicationConfig>
      );
    case PageAuthType.Public:
      return (
        <ApplicationConfig initialViewportSeed={auth.initialViewportSeed}>
          {children}
        </ApplicationConfig>
      );
  }
};

export interface AppProps<P extends Record<string, unknown>> extends NextAppProps<
  CustomAppProps<P>
> {
  readonly Component: AppPageComponent<P>;
  readonly emotionCache?: EmotionCache;
}

/**
 * The NextJS error-page routes ({@link AppPagePartial.isErrorPage}) that render in place under the
 * originally requested URL.
 */
const ErrorPageRoutes = new Set(['/404', '/500']);

/**
 * The public server-error route that a thrown `getServerSideProps` render is resolved to.
 */
const ServerErrorPageRoute = '/500';

/**
 * Resolves the pathname that the active page is looked up from for a render.
 *
 * A NextJS error-page render must resolve from its own error route rather than the request path:
 * the request path still maps to the original page that produced the error, which - when
 * authenticated - has no injected `auth` and would otherwise be treated as a misconfiguration. A
 * `notFound` render already reports the `/404` pathname, whereas a thrown `getServerSideProps`
 * render reports the original request path and is resolved to {@link ServerErrorPageRoute}.
 */
const resolveActivePagePath = (
  router: { readonly asPath: string; readonly pathname: string },
  isErrorPage: boolean,
): string => {
  if (!isErrorPage) {
    return router.asPath;
  }
  return ErrorPageRoutes.has(router.pathname) ? router.pathname : ServerErrorPageRoute;
};

const App = <P extends Record<string, unknown>>({
  Component,
  emotionCache,
  pageProps,
  router,
}: AppProps<P>) => {
  useTTI(router);

  const { asPath, pathname } = router;
  const auth = useMemo(
    () =>
      deriveAuth({
        auth: pageProps.auth ?? null,
        path: resolveActivePagePath({ asPath, pathname }, Component.isErrorPage ?? false),
      }),
    [Component.isErrorPage, pageProps.auth, asPath, pathname],
  );

  return (
    <ErrorBoundary isLti={auth?.type === PageAuthType.Lti}>
      <CacheProvider value={emotionCache ?? clientSideEmotionCache}>
        <Head>
          <title>{auth?.page.title ?? DefaultPageTitle}</title>
          <meta content='initial-scale=1, width=device-width' name='viewport' />
        </Head>
        <EnvironmentProvider environment={auth?.env ?? null}>
          {auth ? (
            <InnerApp auth={auth} Component={Component} pageProps={pageProps} router={router} />
          ) : null}
        </EnvironmentProvider>
      </CacheProvider>
    </ErrorBoundary>
  );
};

export default App;
